import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { partnerSignupUrl } from '../config/partnerPanel';
import { contactInfo } from '../config/contactInfo';
import { PartnerPanelLink } from '../components/PartnerPanelLink';
import { SiteHero, SiteClosingCta, FeatureBadgeRow } from '../components/site';
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Send,
  CheckCircle,
  ArrowRight,
  Building,
  Users,
  Headphones,
  ExternalLink,
} from 'lucide-react';

export const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: '',
    inquiryType: 'general',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const inquiryTypes = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'partnership', label: 'Partnership Opportunity' },
    { value: 'technical', label: 'Technical Support' },
    { value: 'sales', label: 'Sales Inquiry' },
    { value: 'media', label: 'Media & Press' },
  ];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      console.log('Contact form submitted:', formData);
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        subject: '',
        message: '',
        inquiryType: 'general',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="io-site contact-us-page">
        <div className="contact-success">
          <div className="contact-premium__inner">
            <div className="success-content">
              <div className="success-icon">
                <CheckCircle size={72} strokeWidth={1.5} />
              </div>
              <h1>Thank You for Contacting Us!</h1>
              <p>We&apos;ve received your message and will get back to you within 24 hours.</p>
              <div className="success-actions">
                <Link to="/" className="btn btn-primary btn-large">
                  <span>Back to Home</span>
                  <ArrowRight size={20} />
                </Link>
                <button
                  type="button"
                  onClick={() => setIsSubmitted(false)}
                  className="btn btn-outline btn-large"
                >
                  <span>Send Another Message</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="io-site contact-us-page">
      <SiteHero
        centered
        title="Get in Touch"
        subtitle="Reach the QicApp Technologies team for partnerships, platform support, or general inquiries about smart home solutions."
        badges={
          <FeatureBadgeRow
            badges={[
              { label: 'Gurugram HQ' },
              { label: 'Quick Response' },
              { label: 'Expert Guidance' },
            ]}
          />
        }
        primaryCta={{ label: 'Send a Message', to: '#contact-form' }}
      />

      {/* Office + Map */}
      <section className="contact-premium-hub" aria-labelledby="contact-office-title">
        <div className="contact-premium__inner">
          <div className="contact-premium-hub__grid">
            <article className="contact-office-card">
              <p className="contact-office-card__eyebrow">Our Office</p>
              <h2 id="contact-office-title" className="contact-office-card__title">
                {contactInfo.companyName}
              </h2>
              <p className="contact-office-card__building">{contactInfo.buildingName}</p>
              <address className="contact-office-card__address">
                {contactInfo.addressLines.map((line) => (
                  <span key={line}>{line}</span>
                ))}
              </address>
              <div className="contact-office-card__divider" aria-hidden />
              <ul className="contact-office-card__channels">
                <li>
                  <span className="contact-office-card__icon" aria-hidden>
                    <Mail size={20} strokeWidth={1.75} />
                  </span>
                  <div>
                    <span className="contact-office-card__label">Email</span>
                    <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
                  </div>
                </li>
                <li>
                  <span className="contact-office-card__icon" aria-hidden>
                    <Phone size={20} strokeWidth={1.75} />
                  </span>
                  <div>
                    <span className="contact-office-card__label">Phone</span>
                    <a href={`tel:${contactInfo.phoneTel}`}>{contactInfo.phone}</a>
                  </div>
                </li>
              </ul>
              <a
                href={contactInfo.mapsOpenUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary contact-office-card__directions"
              >
                <span>Get Directions</span>
                <ExternalLink size={16} />
              </a>
            </article>

            <div className="contact-map-card">
              <div className="contact-map-card__header">
                <MapPin size={18} strokeWidth={1.75} aria-hidden />
                <span>{contactInfo.mapAddress}</span>
              </div>
              <div className="contact-map-card__frame">
                <iframe
                  title={`${contactInfo.buildingName} on Google Maps`}
                  src={contactInfo.mapsEmbedUrl}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick contact cards */}
      <section className="contact-info-section">
        <div className="contact-premium__inner">
          <header className="contact-section-header">
            <p className="contact-section-header__eyebrow">CONTACT</p>
            <h2>How to reach us</h2>
            <p>Choose the channel that works best for your inquiry</p>
          </header>
          <div className="contact-cards">
            <article className="contact-card">
              <div className="contact-icon">
                <Mail size={28} strokeWidth={1.75} />
              </div>
              <div className="contact-card__body">
                <h3>Email</h3>
                <p className="contact-card__value">
                  <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
                </p>
              </div>
              <div className="contact-meta">
                <span className="meta-label">General Inquiries</span>
              </div>
            </article>
            <article className="contact-card">
              <div className="contact-icon">
                <Phone size={28} strokeWidth={1.75} />
              </div>
              <div className="contact-card__body">
                <h3>Phone</h3>
                <p className="contact-card__value">
                  <a href={`tel:${contactInfo.phoneTel}`}>{contactInfo.phone}</a>
                </p>
              </div>
              <div className="contact-meta">
                <span className="meta-label">Business Line</span>
              </div>
            </article>
            <article className="contact-card">
              <div className="contact-icon">
                <MapPin size={28} strokeWidth={1.75} />
              </div>
              <div className="contact-card__body">
                <h3>Office</h3>
                <p className="contact-card__value contact-card__value--address">
                  {contactInfo.addressLines.map((line) => (
                    <span key={line}>{line}</span>
                  ))}
                </p>
              </div>
              <div className="contact-meta">
                <span className="meta-label">Headquarters</span>
              </div>
            </article>
            <article className="contact-card contact-card--hours">
              <div className="contact-icon">
                <Clock size={28} strokeWidth={1.75} />
              </div>
              <div className="contact-card__body">
                <h3>Business Hours</h3>
                <ul className="contact-card__hours">
                  {contactInfo.businessHours.map((row) => (
                    <li key={row.days}>
                      <span className="contact-card__hours-days">{row.days}:</span>
                      <span className="contact-card__hours-time">{row.hours}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="contact-meta">
                <span className="meta-label">{contactInfo.timezone} Timezone</span>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="contact-form-section">
        <div className="contact-premium__inner">
          <div className="contact-form-layout">
            <div className="contact-form-aside">
              <p className="contact-form-aside__eyebrow">MESSAGE US</p>
              <h2>Send us a message</h2>
              <p>
                Fill out the form and our team will respond within one business day.
              </p>
              <ul className="contact-form-aside__list">
                <li>
                  <Building size={18} aria-hidden />
                  <span>{contactInfo.companyName}</span>
                </li>
                <li>
                  <Mail size={18} aria-hidden />
                  <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
                </li>
                <li>
                  <Phone size={18} aria-hidden />
                  <a href={`tel:${contactInfo.phoneTel}`}>{contactInfo.phone}</a>
                </li>
              </ul>
            </div>
            <div className="form-content">
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`form-input ${errors.name ? 'error' : ''}`}
                      placeholder="Enter your full name"
                      required
                    />
                    {errors.name && <span className="error-message">{errors.name}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`form-input ${errors.email ? 'error' : ''}`}
                      placeholder="Enter your email address"
                      required
                    />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="company" className="form-label">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Enter your company name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="inquiryType" className="form-label">
                      Inquiry Type
                    </label>
                    <select
                      id="inquiryType"
                      name="inquiryType"
                      value={formData.inquiryType}
                      onChange={handleInputChange}
                      className="form-input"
                    >
                      {inquiryTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="subject" className="form-label">
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className={`form-input ${errors.subject ? 'error' : ''}`}
                      placeholder="Enter message subject"
                      required
                    />
                    {errors.subject && <span className="error-message">{errors.subject}</span>}
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className={`form-input textarea ${errors.message ? 'error' : ''}`}
                    placeholder="Tell us about your inquiry or project..."
                    rows={6}
                    required
                  />
                  {errors.message && <span className="error-message">{errors.message}</span>}
                </div>
                <button
                  type="submit"
                  className="btn btn-primary btn-large btn-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner" />
                      Sending Message...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Contact Methods */}
      <section className="additional-contact">
        <div className="contact-premium__inner">
          <header className="contact-section-header">
            <p className="contact-section-header__eyebrow">PARTNERSHIPS</p>
            <h2>Other ways to connect</h2>
            <p>Choose the path that fits your goals</p>
          </header>
          <div className="contact-methods">
            <article className="method-card">
              <div className="method-icon">
                <Users size={36} strokeWidth={1.75} />
              </div>
              <h3>Partnership Inquiries</h3>
              <p>Interested in becoming a partner? Let&apos;s discuss opportunities.</p>
              <PartnerPanelLink to={partnerSignupUrl()} className="btn btn-outline">
                <span>Become a Partner</span>
                <ArrowRight size={16} />
              </PartnerPanelLink>
            </article>
            <article className="method-card">
              <div className="method-icon">
                <Headphones size={36} strokeWidth={1.75} />
              </div>
              <h3>Technical Support</h3>
              <p>Need help with our platform or have technical questions?</p>
              <a href={`mailto:${contactInfo.email}`} className="btn btn-outline">
                <span>Get Support</span>
                <ArrowRight size={16} />
              </a>
            </article>
            <article className="method-card">
              <div className="method-icon">
                <Building size={36} strokeWidth={1.75} />
              </div>
              <h3>Business Development</h3>
              <p>Looking for custom solutions or enterprise partnerships?</p>
              <a href={`mailto:${contactInfo.email}`} className="btn btn-outline">
                <span>Contact BD Team</span>
                <ArrowRight size={16} />
              </a>
            </article>
          </div>
        </div>
      </section>

      <SiteClosingCta
        titleLines={['Questions about', 'partnership?']}
        subtitle="Our team typically responds within one business day"
        cta={{ label: 'Partner Signup', to: '/partner-registration' }}
      />
    </div>
  );
};
